// src/pages/AttendancePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { attendanceAPI, staffAPI, companiesAPI } from '../services/api';
import { Card, Badge, SectionHeader, EmptyState, Button, Select } from '../components/ui';
import { format, subDays } from 'date-fns';
import { Users, Clock, TrendingDown, Download, RefreshCw, AlertTriangle, CheckCircle, Search } from 'lucide-react';
import styles from './AttendancePage.module.css';

// ── Timezone-aware time formatter ─────────────────────────────────────────────
function formatLocalTime(isoString, timezone) {
    if (!isoString) return '?';
    try {
        return new Date(isoString).toLocaleTimeString('en-US', {
            timeZone: timezone || 'America/Tegucigalpa',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    } catch (_) {
        return isoString.slice(11, 16); // fallback to raw UTC slice
    }
}

export default function AttendancePage() {
    const { companyId, isGaafAdmin } = useAuth();
    const [tab, setTab] = useState('report');
    const [report, setReport] = useState(null);
    const [attendance, setAttendance] = useState([]);
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [timezone, setTimezone] = useState('America/Tegucigalpa');
    const [from, setFrom] = useState(format(subDays(new Date(), 14), 'yyyy-MM-dd'));
    const [to, setTo] = useState(format(new Date(), 'yyyy-MM-dd'));

    useEffect(() => {
        if (!isGaafAdmin) return;
        companiesAPI.list().then(res => {
            const list = res.data.companies || [];
            setCompanies(list);
            if (list.length > 0) {
                setSelectedCompanyId(list[0].companyId);
                setTimezone(list[0].settings?.timezone || 'America/Tegucigalpa');
            }
        }).catch(() => { });
    }, [isGaafAdmin]);

    const effectiveCompanyId = isGaafAdmin ? selectedCompanyId : companyId;

    // When company changes, update timezone
    useEffect(() => {
        if (isGaafAdmin && selectedCompanyId) {
            const c = companies.find(x => x.companyId === selectedCompanyId);
            if (c) setTimezone(c.settings?.timezone || 'America/Tegucigalpa');
        }
    }, [selectedCompanyId, companies]);

    // For non-admin, load their company timezone once
    useEffect(() => {
        if (isGaafAdmin || !companyId) return;
        companiesAPI.get(companyId).then(res => {
            const tz = res.data?.company?.settings?.timezone || res.data?.settings?.timezone;
            if (tz) setTimezone(tz);
        }).catch(() => { });
    }, [companyId, isGaafAdmin]);

    const loadReport = useCallback(async () => {
        if (!effectiveCompanyId || !from || !to) return;
        setLoading(true);
        try {
            const res = await attendanceAPI.getReport({ companyId: effectiveCompanyId, from, to });
            setReport(res.data);
        } catch (_) { }
        setLoading(false);
    }, [effectiveCompanyId, from, to]);

    const loadAttendance = useCallback(async () => {
        if (!effectiveCompanyId) return;
        setLoading(true);
        try {
            const res = await attendanceAPI.get({ companyId: effectiveCompanyId, from, to });
            setAttendance(res.data.attendance || []);
        } catch (_) { }
        setLoading(false);
    }, [effectiveCompanyId, from, to]);

    useEffect(() => {
        if (tab === 'report') loadReport();
        else loadAttendance();
    }, [tab, loadReport, loadAttendance]);

    const downloadCSV = () => {
        if (!report?.staff) return;
        const headers = ['Name', 'Employee ID', 'Position', 'Days Present', 'Days Absent', 'Hours Worked', 'Hours Expected', 'Hours Shortfall', 'Flagged Days', 'Pay Owed', 'Deductions', 'Recommended Pay', 'Currency', 'Note'];
        const rows = report.staff.map(s => [
            s.fullName, s.employeeId, s.position, s.presentDays, s.absentDays,
            s.totalHoursWorked, s.totalHoursExpected, s.totalHoursShortfall,
            s.flaggedDays, s.payOwed, s.deductions, s.recommendedPay, s.currency, `"${s.payrollNote}"`,
        ]);
        const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attendance-report-${from}-${to}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="Attendance & Payroll"
                description={`RFID Badge Tracking — Times shown in ${timezone}`}
                actions={
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        {isGaafAdmin && companies.length > 0 && (
                            <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}
                                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '7px 12px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', outline: 'none' }}>
                                {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                            </select>
                        )}
                        <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '7px 10px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', outline: 'none' }} />
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>to</span>
                        <input type="date" value={to} onChange={e => setTo(e.target.value)}
                            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '7px 10px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', outline: 'none' }} />
                        {tab === 'report' && report && (
                            <Button variant="secondary" size="sm" icon={<Download size={14} />} onClick={downloadCSV}>
                                Export CSV
                            </Button>
                        )}
                        <Button variant="secondary" size="sm" icon={<RefreshCw size={14} className={loading ? 'animate-spin' : ''} />}
                            onClick={tab === 'report' ? loadReport : loadAttendance} disabled={loading}>
                            Refresh
                        </Button>
                    </div>
                }
            />

            <div className={styles.tabs}>
                <button className={`${styles.tab} ${tab === 'report' ? styles.tabActive : ''}`} onClick={() => setTab('report')}>
                    Payroll Report
                </button>
                <button className={`${styles.tab} ${tab === 'daily' ? styles.tabActive : ''}`} onClick={() => setTab('daily')}>
                    Daily Attendance
                </button>
            </div>

            {tab === 'report' ? (
                <ReportView report={report} loading={loading} timezone={timezone} />
            ) : (
                <DailyView records={attendance} loading={loading} timezone={timezone} />
            )}
        </div>
    );
}

function ReportView({ report, loading, timezone }) {
    const [search, setSearch] = useState('');
    if (loading) return <div className={styles.loadingMsg}>Loading Report…</div>;
    if (!report) return null;
    const { summary, staff } = report;
    const filteredStaff = !search ? (staff || []) : (staff || []).filter(s =>
        s.fullName?.toLowerCase().includes(search.toLowerCase()) ||
        s.position?.toLowerCase().includes(search.toLowerCase()) ||
        s.employeeId?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className={styles.summaryGrid}>
                <Card className={styles.summaryCard}>
                    <div className={styles.summaryLabel}>Staff Members</div>
                    <div className={styles.summaryNum}>{summary.totalStaff}</div>
                </Card>
                <Card className={styles.summaryCard}>
                    <div className={styles.summaryLabel}>Hours Worked</div>
                    <div className={styles.summaryNum}>{Number(summary.totalHoursWorked).toFixed(0)}</div>
                </Card>
                <Card className={styles.summaryCard}>
                    <div className={styles.summaryLabel}>Total Pay Owed</div>
                    <div className={styles.summaryNum}>{summary.currency} {Number(summary.totalPayOwed).toLocaleString()}</div>
                </Card>
                <Card className={styles.summaryCard}>
                    <div className={styles.summaryLabel}>Total Deductions</div>
                    <div className={styles.summaryNum} style={{ color: '#e09d5a' }}>{summary.currency} {Number(summary.totalDeductions).toLocaleString()}</div>
                </Card>
            </div>

            {(!filteredStaff || filteredStaff.length === 0) ? (
                <EmptyState icon={<Users size={40} />}
                    title={search ? 'No Staff Members match your search' : 'No Staff Data for this period'}
                    description={search ? 'Try a different Name, Position, or Employee ID.' : 'Make sure Staff Members have been added and have RFID Badges.'} />
            ) : (
                <>
                    <div style={{ position: 'relative', maxWidth: 360, marginBottom: 12 }}>
                        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search Staff Members…"
                            style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {filteredStaff.map(s => <StaffReportCard key={s.staffId} staff={s} timezone={timezone} />)}
                    </div>
                </>
            )}
        </div>
    );
}

function StaffReportCard({ staff, timezone }) {
    const [expanded, setExpanded] = useState(false);
    const attendancePct = staff.workingDaysInPeriod > 0
        ? Math.round((staff.presentDays / staff.workingDaysInPeriod) * 100)
        : 0;

    return (
        <Card className={styles.staffCard}>
            <div className={styles.staffCardHeader} onClick={() => setExpanded(p => !p)}>
                <div className={styles.staffMeta}>
                    <div className={styles.staffName}>{staff.fullName}</div>
                    <div className={styles.staffSub}>{staff.position} · ID: {staff.employeeId}</div>
                </div>
                <div className={styles.staffStats}>
                    <div className={styles.stat}>
                        <span className={styles.statNum}>{attendancePct}%</span>
                        <span className={styles.statLbl}>Attendance</span>
                    </div>
                    <div className={styles.stat}>
                        <span className={styles.statNum}>{staff.totalHoursWorked}h</span>
                        <span className={styles.statLbl}>Worked</span>
                    </div>
                    <div className={styles.stat}>
                        <span className={styles.statNum} style={{ color: 'var(--green-300)' }}>
                            {staff.currency} {Number(staff.recommendedPay).toLocaleString()}
                        </span>
                        <span className={styles.statLbl}>Recommended Pay</span>
                    </div>
                    {staff.flaggedDays > 0 && (
                        <Badge variant="warning" size="sm"><AlertTriangle size={11} style={{ marginRight: 3 }} />{staff.flaggedDays} Flagged</Badge>
                    )}
                </div>
            </div>

            <div className={styles.payrollNote}>
                {staff.flaggedDays === 0 && staff.absentDays === 0
                    ? <CheckCircle size={14} style={{ color: 'var(--green-400)' }} />
                    : <AlertTriangle size={14} style={{ color: '#e09d5a' }} />
                }
                <span>{staff.payrollNote}</span>
            </div>

            {expanded && (
                <div className={styles.expandedArea}>
                    {staff.absentDays > 0 && (
                        <div className={styles.absenceNote}>
                            <strong>ℹ Note:</strong> {staff.absentDays} absent day{staff.absentDays !== 1 ? 's' : ''} out of {staff.workingDaysInPeriod} working days in this period.
                            Hours expected only counts days where badge reads were recorded.
                        </div>
                    )}
                    <div className={styles.payBreakdown}>
                        <div className={styles.payRow}><span>Working Days in Period</span><span>{staff.workingDaysInPeriod} Days</span></div>
                        <div className={styles.payRow}><span>Days Present</span><span style={{ color: 'var(--green-300)' }}>{staff.presentDays} Days</span></div>
                        <div className={styles.payRow}><span>Days Absent</span><span style={{ color: staff.absentDays > 0 ? '#e09d5a' : 'inherit' }}>{staff.absentDays} Days</span></div>
                        <div className={styles.payRow}><span>Hourly Rate</span><span>{staff.currency} {Number(staff.hourlyRate).toFixed(2)}/hr</span></div>
                        <div className={styles.payRow}><span>Hours Worked</span><span>{staff.totalHoursWorked}h</span></div>
                        <div className={styles.payRow}><span>Hours Expected ⓘ</span><span>{staff.totalHoursExpected}h</span></div>
                        <div className={styles.payRow}><span>Hours Shortfall</span><span style={{ color: '#e09d5a' }}>{staff.totalHoursShortfall}h</span></div>
                        <div className={styles.payRow}><span>Gross Pay</span><span>{staff.currency} {Number(staff.payOwed).toLocaleString()}</span></div>
                        <div className={styles.payRow}><span>Deductions</span><span style={{ color: '#e07a7a' }}>- {staff.currency} {Number(staff.deductions).toLocaleString()}</span></div>
                        <div className={`${styles.payRow} ${styles.payTotal}`}><span>Recommended Pay</span><span style={{ color: 'var(--green-300)' }}>{staff.currency} {Number(staff.recommendedPay).toLocaleString()}</span></div>
                    </div>

                    {staff.dailyRecords?.length > 0 && (
                        <div className={styles.dailyBreakdown}>
                            <div className={styles.dailyHeader}>Daily Breakdown</div>
                            {staff.dailyRecords.map(day => (
                                <div key={day.date} className={`${styles.dayRow} ${day.hasSignificantAbsences ? styles.dayFlagged : ''}`}>
                                    <span className={styles.dayDate}>{day.date}</span>
                                    <Badge variant={day.status === 'present' ? 'success' : 'danger'} size="sm">{day.status}</Badge>
                                    <span className={styles.dayHours}>{day.totalHoursWorked}h</span>
                                    {day.checkInFormatted && (
                                        <span className={styles.dayTime}>
                                            {formatLocalTime(day.checkInFormatted, timezone)} – {day.checkOutFormatted ? formatLocalTime(day.checkOutFormatted, timezone) : '?'}
                                        </span>
                                    )}
                                    {day.exitCount > 0 && <span className={styles.dayExits}>{day.exitCount} exit{day.exitCount > 1 ? 's' : ''} ({day.totalMinutesOutside}min)</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
}

function DailyView({ records, loading, timezone }) {
    const [search, setSearch] = useState('');
    if (loading) return <div className={styles.loadingMsg}>Loading Attendance…</div>;
    if (records.length === 0) return (
        <EmptyState icon={<Clock size={40} />} title="No Attendance Records" description="Attendance is recorded automatically as Staff Badges pass through the RFID Antennas." />
    );

    // Filter by staffId before grouping by date
    const searchedRecords = !search ? records : records.filter(r =>
        r.staffId?.toLowerCase().includes(search.toLowerCase()) ||
        r.staffName?.toLowerCase().includes(search.toLowerCase()) ||
        r.fullName?.toLowerCase().includes(search.toLowerCase())
    );

    const byDate = {};
    searchedRecords.forEach(r => {
        if (!byDate[r.date]) byDate[r.date] = [];
        byDate[r.date].push(r);
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ position: 'relative', maxWidth: 360 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by Name or Staff ID…"
                    style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            {Object.keys(byDate).length === 0 ? (
                <EmptyState icon={<Clock size={40} />} title="No Records match your search" description="Try a different Staff Name or ID." />
            ) : (
                Object.entries(byDate).sort((a, b) => b[0].localeCompare(a[0])).map(([date, recs]) => (
                    <Card key={date} padding={false}>
                        <div className={styles.dateHeader}>
                            <strong>{date}</strong>
                            <span className={styles.dateSub}>{recs.filter(r => r.status === 'present').length}/{recs.length} Present</span>
                        </div>
                        {recs.map(rec => (
                            <div key={rec.attendanceId} className={styles.attendanceRow}>
                                <Badge variant={rec.status === 'present' ? 'success' : 'danger'} size="sm">{rec.status}</Badge>
                                <span className={styles.attName}>{rec.staffName || rec.staffId}</span>
                                {rec.checkInFormatted && (
                                    <span className={styles.attTime}>
                                        In: {formatLocalTime(rec.checkInFormatted, timezone)}
                                    </span>
                                )}
                                {rec.checkOutFormatted && (
                                    <span className={styles.attTime}>
                                        Out: {formatLocalTime(rec.checkOutFormatted, timezone)}
                                    </span>
                                )}
                                <span className={styles.attHours}>{rec.totalHoursWorked}h</span>
                                {rec.hasSignificantAbsences && <Badge variant="warning" size="sm">{rec.exitCount} Exits</Badge>}
                            </div>
                        ))}
                    </Card>
                ))
            )}
        </div>
    );
}
